/**
 * The store: a value, a way to change it, and a way to hear that it changed.
 *
 * One of these per screen (`docs/plan.md` M5 task 1). It holds what the screen
 * is showing -- what came back from the API, what the player has picked so far
 * -- and nothing else. No game rule lives in a store: the web app sends a
 * direction or an id and shows what comes back (`AGENTS.md`, server authority).
 */

export type Store<S extends object> = {
  /** The state as it stands. */
  get(): S
  /** Merge a patch and tell the listeners. */
  set(patch: Partial<S>): void
  /** Put the state back as it started, for a screen being entered afresh. */
  reset(): void
  watch(listener: (state: S) => void): () => void
}

export function createStore<S extends object>(initial: S): Store<S> {
  const listeners = new Set<(state: S) => void>()
  let state: S = { ...initial }

  const announce = () => {
    for (const listener of listeners) listener(state)
  }

  return {
    get: () => state,
    set(patch) {
      state = { ...state, ...patch }
      announce()
    },
    reset() {
      state = { ...initial }
      announce()
    },
    watch(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
